import { LocalNotifications } from '@capacitor/local-notifications';
import { Capacitor } from '@capacitor/core';

export type BookingRef = {
    bookingId: number;
    serviceName?: string;
    coachName?: string;
    startISO: string; // Ej: 2025-11-07T18:30:00+01:00
};

const CHANNEL_ID = 'reminders';

async function ensureNotifPermission() {
    if (Capacitor.getPlatform() === 'web') return false;
    const perm = await LocalNotifications.checkPermissions();
    if (perm.display === 'granted') return true;
    const req = await LocalNotifications.requestPermissions();
    return req.display === 'granted';
}

function bookingNotifId(bookingId: number) {
    return Number(`${bookingId}60`);
}

function formatStart(start: Date) {
    return start.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
}

export async function ensureAndroidNotifChannel() {
    if (Capacitor.getPlatform() !== 'android') return;
    try {
        await LocalNotifications.createChannel({
            id: CHANNEL_ID,
            name: 'Recordatorios',
            description: 'Avisos de proximas sesiones',
            importance: 5,
            vibration: true,
        });
    } catch (e) {
        console.warn('No se pudo crear canal reminders:', e);
    }
}

export async function scheduleOneBookingReminder({ bookingId, serviceName, startISO }: BookingRef) {
    if (Capacitor.getPlatform() === 'web') return false;
    try {
        const granted = await ensureNotifPermission();
        if (!granted) return false;

        const start = new Date(startISO);
        if (isNaN(start.getTime())) return false;

        const fireDate = new Date(start.getTime() - 60 * 60 * 1000); // 1h antes
        if (fireDate.getTime() <= Date.now()) return false;

        await ensureAndroidNotifChannel();

        const id = bookingNotifId(bookingId);
        const startHour = formatStart(start);
        const body = serviceName
            ? `${serviceName} empieza a las ${startHour}`
            : `Tu sesion comienza a las ${startHour}`;

        await LocalNotifications.schedule({
            notifications: [{
            id,
            title: 'Recordatorio de sesion',
            body,
            schedule: { at: fireDate, allowWhileIdle: true },
            channelId: CHANNEL_ID,
            extra: { type: 'booking_reminder', bookingId: String(bookingId), minutes_before: 60 },
            }],
        });
        return true;
    } catch (e) {
        console.warn('Error scheduling booking reminder:', e);
        return false;
    }
}

export async function scheduleManyBookingReminders(items: BookingRef[]) {
    for (const it of items) await scheduleOneBookingReminder(it);
}

export async function cancelBookingReminder(bookingId: number) {
    await LocalNotifications.cancel({ notifications: [{ id: bookingNotifId(bookingId) }] });
}

export async function cancelAllBookingReminders() {
    const pending = await LocalNotifications.getPending();
    const bookingNotifs = pending.notifications.filter((n) => {
        const type = (n as any)?.extra?.type;
        if (type === 'booking_reminder') return true;
        const idStr = String((n as any)?.id ?? '');
        return idStr.endsWith('60');
    });
    if (bookingNotifs.length) {
        await LocalNotifications.cancel({ notifications: bookingNotifs.map(n => ({ id: n.id })) });
    }
}

export async function rescheduleBookingReminder(b: BookingRef) {
    await cancelBookingReminder(b.bookingId);
    return scheduleOneBookingReminder(b);
}

export async function scheduleTestBookingReminder(bookingId: number, serviceName?: string) {
    const perm = await LocalNotifications.checkPermissions();
    if (perm.display !== 'granted') {
        const req = await LocalNotifications.requestPermissions();
        if (req.display !== 'granted') return false;
    }
    const id = Number(`${bookingId}999`);
    const in30s = new Date(Date.now() + 30 * 1000);
    await LocalNotifications.schedule({
        notifications: [{
        id,
        title: 'Prueba de recordatorio',
        body: serviceName ? `Esto es una prueba para ${serviceName}` : 'Esto es una prueba.',
        schedule: { at: in30s, allowWhileIdle: true },
        extra: { type: 'booking_reminder_test', bookingId: String(bookingId) },
        }],
    });
    return true;
}
